import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Slider } from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { Cross } from "components/icons/Cross";
import {
  CHAT_CARD_TYPE,
  ESIGN_REQUEST_CUSTOM_TYPE,
  MERCHANT_EMAIL_REQUEST_CUSTOM_TYPE,
  OPEN_ITEM_CUSTOM_TYPE,
  REQUESTED_ANSWERS_CUSTOM_TYPE,
  REQUESTED_DOCUMENTS_CUSTOM_TYPE,
  REQUESTED_INFO_CUSTOM_TYPE,
  REQUESTED_REVIEW_DRAFT_TYPE,
  REQUESTED_DFT_REVIEW_TYPE,
  SENSITIVE_DATA_CUSTOM_TYPE,
  PRICED_TICKET_CARD,
  ACCOUNTANT_ACCESS_REQUEST_CARD_TYPE,
} from "constants/chatType";
import { usePaginatedMessages } from "hooks/usePaginatedMessages";
import { useDispatch, useSelector } from "react-redux";
import {
  closeActionCentreSlider,
  closeTicketSlider,
} from "store/slices/channelInfoSlider";
import { RootState } from "store/store";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { ActionCentreList } from "./ActionCentreList";
import { useState } from "react";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ChatActionCentreSliderWrapper } from "./ChatActionCentreSliderWrapper";
import {
  ActionCentreMessage,
  ArchiveActionCentreItemModal,
} from "./ArchiveActionCentreItemModal";

export const ChatActionCentreSlider = ({
  block = false,
  className,
  handleSliderClose,
  withSlider = true,
}: {
  block?: boolean;
  className?: string;
  handleSliderClose?: () => void;
  withSlider?: boolean;
}) => {
  const { client } = useChatContext();
  const { channel } = useChannelStateContext();
  const dispatch = useDispatch();
  const sliderOpen = useSelector(
    (store: RootState) => store.channelInfoSlider.showActionCentreSlier
  );
  const groupId = channel.data?.group_uuid! as string;
  const [selectTab, setSelectedTab] = useState<
    "PENDING" | "COMPLETED" | "DEPRECATED"
  >("PENDING");
  const { isAdmin } = useRoleBasedView();

  let cardStatusFilter = "";
  if (selectTab === "PENDING") {
    cardStatusFilter = "REQUESTED";
  }
  if (selectTab === "COMPLETED") {
    cardStatusFilter = "SUBMITTED";
  }
  if (selectTab === "DEPRECATED") {
    cardStatusFilter = "DEPRECATED";
  }

  const actionCentreSearchFilters = {
    custom_type: {
      $in: [
        CHAT_CARD_TYPE,
        OPEN_ITEM_CUSTOM_TYPE,
        REQUESTED_DOCUMENTS_CUSTOM_TYPE,
        MERCHANT_EMAIL_REQUEST_CUSTOM_TYPE,
        REQUESTED_INFO_CUSTOM_TYPE,
        SENSITIVE_DATA_CUSTOM_TYPE,
        REQUESTED_ANSWERS_CUSTOM_TYPE,
        ESIGN_REQUEST_CUSTOM_TYPE,
        REQUESTED_REVIEW_DRAFT_TYPE,
        REQUESTED_DFT_REVIEW_TYPE,
        PRICED_TICKET_CARD,
        ACCOUNTANT_ACCESS_REQUEST_CARD_TYPE,
      ],
    },
    card_status: { $eq: cardStatusFilter },
  };

  const actionMessageData = usePaginatedMessages(
    client,
    {
      id: channel.id,
    },
    {},
    { limit: 100 },
    actionCentreSearchFilters
  );

  const { messages, loadNextPage, next, loading, setMessages } =
    actionMessageData;
  const [statusUpdateMessage, setStatusUpdateMessage] =
    useState<ActionCentreMessage | null>(null);

  const onOutslideSliderClick = () => {
    if (sliderOpen) {
      dispatch(closeActionCentreSlider());
      handleSliderClose?.();
    }
  };

  const reloadActionCentreMessages = async () => {
    const response = await client.search(
      { id: channel.id },
      actionCentreSearchFilters,
      { limit: 100, sort: { updated_at: -1 } }
    );
    setMessages(response.results);
  };

  return (
    <ChatActionCentreSliderWrapper
      sliderOpen={sliderOpen}
      block={block}
      className={className}
      withSlider={withSlider}
    >
      <Tab.Root defaultValue="PENDING" className="t-h-full">
        <DashboardContainer className="t-h-full">
          <DashboardContainer.Header>
            <div className="t-py-3 t-pr-4 t-pl-5 t-flex t-justify-between t-items-center ">
              <div className="t-flex t-gap-2 t-items-center">
                <Button
                  customType="ghost_icon"
                  size="small"
                  onClick={onOutslideSliderClick}
                >
                  <span className="t-text-text-30">
                    <ArrowLeft size="16" />
                  </span>
                </Button>
                <span className="t-text-subtitle">Action Centre</span>
              </div>
              <Button
                customType="ghost_icon"
                size="small"
                onClick={onOutslideSliderClick}
              >
                <Cross />
              </Button>
            </div>

            <div className="t-flex t-flex-col t-gap-4 t-px-5 t-pt-0 t-border-b t-border-0 t-border-solid t-border-i-neutral-10">
              <Tab.List className="t-flex t-gap-2">
                <Tab.Trigger
                  value="PENDING"
                  onClick={() => setSelectedTab("PENDING")}
                >
                  <span className="t-text-body-sm">Pending</span>
                </Tab.Trigger>
                <Tab.Trigger
                  value="COMPLETED"
                  onClick={() => setSelectedTab("COMPLETED")}
                >
                  <span className="t-text-body-sm">Completed</span>
                </Tab.Trigger>
                {isAdmin && (
                  <Tab.Trigger
                    value="DEPRECATED"
                    onClick={() => setSelectedTab("DEPRECATED")}
                  >
                    <span className="t-text-body-sm">Archived</span>
                  </Tab.Trigger>
                )}
              </Tab.List>
            </div>
          </DashboardContainer.Header>
          <DashboardContainer.Content
            className="t-p-5 t-pt-4"
            id="media-scroll"
          >
            <div className="t-flex t-flex-col t-gap-4 t-h-full">
              <div className="t-h-full">
                <Tab.Content value="PENDING" className="t-h-full">
                  <ActionCentreList
                    //  @ts-ignore
                    actionMessageItems={messages.map((ticket) => ({
                      message: {
                        ...ticket.message,
                        custom_data: ticket.message.custom_data || {},
                      },
                    }))}
                    loadNextPage={loadNextPage}
                    next={Boolean(next)}
                    groupId={groupId}
                    emptyStateText="No actions pending"
                    onStatusUpdateClick={setStatusUpdateMessage}
                  />
                </Tab.Content>
                <Tab.Content value="COMPLETED" className="t-h-full">
                  <ActionCentreList
                    //  @ts-ignore
                    actionMessageItems={messages.map((ticket) => ({
                      message: {
                        ...ticket.message,
                        custom_data: ticket.message.custom_data || {},
                      },
                    }))}
                    loadNextPage={loadNextPage}
                    next={Boolean(next)}
                    groupId={groupId}
                    completed
                    emptyStateText="No actions completed"
                    onStatusUpdateClick={setStatusUpdateMessage}
                  />
                </Tab.Content>
                <Tab.Content value="DEPRECATED" className="t-h-full">
                  <ActionCentreList
                    //  @ts-ignore
                    actionMessageItems={messages.map((ticket) => ({
                      message: {
                        ...ticket.message,
                        custom_data: ticket.message.custom_data || {},
                      },
                    }))}
                    loadNextPage={loadNextPage}
                    next={Boolean(next)}
                    groupId={groupId}
                    completed
                    emptyStateText="No actions archived"
                    onStatusUpdateClick={setStatusUpdateMessage}
                  />
                </Tab.Content>
              </div>
            </div>
          </DashboardContainer.Content>
        </DashboardContainer>
      </Tab.Root>
      <ArchiveActionCentreItemModal
        message={statusUpdateMessage}
        groupId={groupId}
        onClose={() => setStatusUpdateMessage(null)}
        onSuccess={reloadActionCentreMessages}
      />
    </ChatActionCentreSliderWrapper>
  );
};
